import { mappedColors } from '@pixee/design-system'
import {
  AccessorKeyColumnDef,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Table as ReactTable,
  Row,
  RowData,
  RowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import classNames from 'classnames'
import { Fragment, type ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { GET_TABLE_ROW_TEST_ID } from '../../utils/test-id.ts'
import { Checkbox } from '../input/checkbox'
import { type ActionButtonProps, BulkActionBar } from './bulk-action-bar.tsx'
import { PaginationControls } from './pagination-controls'
import { isInteractiveElementClick } from './row-interaction'
import { TableBodySkeleton } from './table-body-skeleton'
import * as styles from './table.css.ts'

declare module '@tanstack/react-table' {
  // These are needed to override the type definitions for the table.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    width: string
    className?: string
  }

  interface TableState {
    isBulkActionPending?: boolean
  }

  // TODO: Open an issue on the react-table repo to allow typing meta property as required instead of optional
  // interface ColumnDefBase<TData extends RowData, TValue> {
  //   meta: ColumnMeta<TData, TValue>
  // }
}

export type RowDataWithId<T extends RowData> = T & { id: string }

export type Pagination = { pageIndex: number; pageSize: number }

export type ActionButtonConfig<T extends RowData> = {
  /** Button text. Can be a string or a function that receives the selected count for dynamic text. */
  buttonText: string | ((selectedCount: number) => string)
  buttonType?: ActionButtonProps['buttonType']
  icon?: ReactNode
  /** Called when the button is clicked. Receives the IDs of selected rows. */
  onAction: (rowIds: string[]) => Promise<unknown>
  /** Determines if the button should be disabled. Defaults to enabled if not provided. */
  isDisabled?: (data: T[], selectedIds: string[]) => boolean
  /** Tooltip content shown when the button is disabled. */
  disabledTooltip?: ReactNode | ((data: T[], selectedIds: string[]) => ReactNode)
  /** Whether the action triggered by this button is pending. Pass from mutation's isPending state. */
  isPending: boolean
}

export type BulkActionFeature<T extends RowData> = {
  /** Configures buttons rendered in the bulk action bar. */
  actionButtonConfigs: ActionButtonConfig<T>[]
  /** Determines if the row's checkbox is visible. Rows returning false won't show a checkbox. */
  isRowCheckboxVisible: (row: T) => boolean
  /** Determines if the row's checkbox is disabled. Disabled checkboxes are visible but not interactive. */
  isRowCheckboxDisabled?: (row: T) => boolean
}

type TableProps<T extends RowData> = {
  data: T[]
  columns: (ColumnDef<T> | AccessorKeyColumnDef<T>)[]
  isLoading?: boolean
  tableName?: string
  /**
   * Total number of items in the table. If provided, the table will show pagination controls. If omitted, all data will be rendered on a single page.
   */
  totalItems?: number
  /**
   /**
   * If provided, the table will show pagination controls. If omitted, all data will be rendered on a single page.
   */
  pagination?: Pagination
  /**
   * If provided, the table will show pagination controls. If omitted, all data will be rendered on a single page.
   */
  onPaginationChange?: (pagination: Pagination) => unknown
  getRowClassName?: (row: Row<T>) => string
  getRowId: (row: T) => string
  highlightedRowId?: string
  /**
   * If provided, enables checkbox column for selecting rows and bulk action bar for performing actions on selected rows.
   */
  bulkActionFeature?: BulkActionFeature<T>
  rowColor?: {
    even: string
    odd: string
  }
  /**
   * Optional react-table expanded row model.
   * Pass the result of calling getExpandedRowModel() from @tanstack/react-table.
   * Omitting this prop disables expansion entirely.
   */
  getExpandedRowModel?: (table: ReactTable<T>) => () => RowModel<T>
  /**
   * Optional callback to retrieve sub-rows for a given row.
   * Used by react-table to build the expanded row model.
   */
  getSubRows?: (originalRow: T, index: number) => T[] | undefined
  /**
   * Optional callback to determine if a row can be expanded.
   * Used by react-table to control expand/collapse icons.
   */
  getRowCanExpand?: (row: Row<T>) => boolean
  /**
   * Optional callback invoked when a row is clicked.
   * Attaches onClick, keyboard support (Enter/Space), and tabIndex to the `<tr>`.
   */
  onRowClick?: (
    row: Row<T>,
    event: React.MouseEvent<HTMLTableRowElement> | React.KeyboardEvent<HTMLTableRowElement>
  ) => unknown
  emptyStateMessage?: string
}

type SelectionPageMap = Record<string, number>

const buildUpdatedPageMap = (
  prevMap: SelectionPageMap,
  prev: RowSelectionState,
  next: RowSelectionState,
  pageIndex: number
): SelectionPageMap => {
  const removedIds = new Set(Object.keys(prev).filter(id => !next[id]))
  const kept = Object.fromEntries(Object.entries(prevMap).filter(([id]) => !removedIds.has(id)))
  const added = Object.keys(next).reduce<SelectionPageMap>((acc, id) => {
    if (!prev[id]) {
      acc[id] = pageIndex
    }
    return acc
  }, {})
  return { ...kept, ...added }
}

const useRowSelectionWithPageTracking = (pageIndex: number | undefined) => {
  const [rowSelection, setRowSelectionRaw] = useState<RowSelectionState>({})
  const [selectionPageMap, setSelectionPageMap] = useState<SelectionPageMap>({})

  const setRowSelection = useCallback(
    (updaterOrValue: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      setRowSelectionRaw(prev => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue

        if (pageIndex !== undefined) {
          setSelectionPageMap(prevMap => buildUpdatedPageMap(prevMap, prev, next, pageIndex))
        }

        return next
      })
    },
    [pageIndex]
  )

  const selectedPageCount = useMemo(() => {
    if (pageIndex === undefined) {
      return undefined
    }
    return new Set(Object.values(selectionPageMap)).size
  }, [selectionPageMap, pageIndex])

  return { rowSelection, setRowSelection, selectedPageCount }
}

export const Table = <T extends RowData>({
  data,
  columns,
  getRowId,
  isLoading = false,
  tableName,
  totalItems,
  pagination,
  onPaginationChange,
  getRowClassName,
  highlightedRowId,
  bulkActionFeature,
  rowColor = {
    even: mappedColors.table.evenRow,
    odd: mappedColors.table.oddRow,
  },
  getExpandedRowModel,
  getSubRows,
  getRowCanExpand,
  onRowClick,
  emptyStateMessage,
}: TableProps<T>) => {
  const lastSelectedRowIndexRef = useRef<number | null>(null)
  const tableColumns = useWithSelectionColumn(columns, bulkActionFeature, isLoading, lastSelectedRowIndexRef)
  const { rowSelection, setRowSelection, selectedPageCount } = useRowSelectionWithPageTracking(pagination?.pageIndex)

  const table = useReactTable<T>({
    data,
    getRowId,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel,
    getSubRows,
    getRowCanExpand,
    enableRowSelection: getEnableRowSelectionForMainRowsOnly(bulkActionFeature),
    enableMultiRowSelection: !!bulkActionFeature,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      isBulkActionPending: bulkActionFeature?.actionButtonConfigs.some(a => a.isPending),
    },
  })

  const rows = table.getRowModel().rows

  return (
    <div style={{ position: 'relative' }}>
      <table className={styles.table} role="table" aria-label={`${tableName} table`}>
        <colgroup>
          {table.options.columns.map((column, index) => (
            <col key={index} style={{ width: column.meta?.width }} />
          ))}
        </colgroup>
        <thead className={styles.tableHead}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <th key={header.id} className={classNames(styles.columnHeader, styles.cell)}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        {isLoading ? (
          <TableBodySkeleton
            rowCount={pagination?.pageSize ?? 7}
            columnCount={table.options.columns.length}
            rowHeightInPixels={43}
            label={`Loading ${tableName} data...`}
          />
        ) : (
          <>
            <tbody>
              {table.options.data.length === 0 ? (
                <tr>
                  <td className={classNames(styles.cell, styles.emptyStateCell)} colSpan={table.options.columns.length}>
                    <div className={styles.emptyStateText}>{emptyStateMessage ?? 'No data yet, stay tuned'}</div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className={classNames(styles.cell, styles.emptyStateCell)} colSpan={table.options.columns.length}>
                    <div className={styles.emptyStateText}>No results found</div>
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <Fragment key={row.id}>
                    <tr
                      className={classNames(styles.row, getRowClassName?.(row), {
                        [styles.selectedRow]: row.id === highlightedRowId,
                        [styles.unselectedRow]: row.id !== highlightedRowId && highlightedRowId !== undefined,
                      })}
                      data-testid={GET_TABLE_ROW_TEST_ID(row.id)}
                      data-test-selected={row.id === highlightedRowId}
                      style={assignInlineVars({
                        [styles.oddRowColor]: rowColor.odd,
                        [styles.evenRowColor]: rowColor.even,
                      })}
                      onClick={onRowClick ? e => onRowClick(row, e) : undefined}
                      onKeyDown={
                        onRowClick
                          ? e => {
                              if (e.key !== 'Enter' && e.key !== ' ') {
                                return
                              }
                              // Let interactive descendants keep native key handling
                              // (checked before preventDefault).
                              if (isInteractiveElementClick(e)) {
                                return
                              }
                              e.preventDefault()
                              onRowClick(row, e)
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className={classNames(styles.cell, cell.column.columnDef.meta?.className)}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))
              )}
            </tbody>
            {pagination && onPaginationChange && tableName && typeof totalItems === 'number' && (
              <tfoot>
                <tr>
                  <th colSpan={table.options.columns.length} className={styles.paginationCell}>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                      <PaginationControls
                        tableName={tableName}
                        pageIndex={pagination.pageIndex}
                        pageSize={pagination.pageSize}
                        totalItems={totalItems}
                        onPageIndexChange={pageIndex =>
                          onPaginationChange({ pageIndex, pageSize: pagination.pageSize })
                        }
                        onPageSizeChange={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
                      />
                    </div>
                  </th>
                </tr>
              </tfoot>
            )}
          </>
        )}
      </table>
      {bulkActionFeature && Object.keys(rowSelection).length > 0 && (
        <BulkActionBar
          selectedCount={Object.keys(rowSelection).length}
          selectedPageCount={selectedPageCount}
          actionButtonsProps={bulkActionFeature.actionButtonConfigs.map(action => {
            const selectedIds = Object.keys(rowSelection)
            const selectedCount = selectedIds.length

            return {
              buttonText:
                typeof action.buttonText === 'function' ? action.buttonText(selectedCount) : action.buttonText,
              buttonType: action.buttonType,
              icon: action.icon,
              onAction: async () => {
                await action.onAction(selectedIds)
                table.resetRowSelection()
              },
              isDisabled: action.isDisabled?.(data, selectedIds) ?? false,
              disabledTooltip:
                typeof action.disabledTooltip === 'function'
                  ? action.disabledTooltip(data, selectedIds)
                  : action.disabledTooltip,
              isPending: action.isPending,
            }
          })}
          onClear={() => table.resetRowSelection()}
        />
      )}
    </div>
  )
}

const useWithSelectionColumn = <T extends RowData>(
  columns: (ColumnDef<T> | AccessorKeyColumnDef<T>)[],
  bulkActionFeature: BulkActionFeature<T> | undefined,
  isLoading: boolean,
  lastSelectedRowIndexRef: React.RefObject<number | null>
) => {
  return useMemo(
    () =>
      bulkActionFeature
        ? [getSelectionColumnDef<T>({ bulkActionFeature, isLoading, lastSelectedRowIndexRef }), ...columns]
        : columns,
    [columns, bulkActionFeature, isLoading, lastSelectedRowIndexRef]
  )
}

const getSelectionColumnDef = <T extends RowData>({
  bulkActionFeature: { isRowCheckboxVisible, isRowCheckboxDisabled = () => false },
  isLoading,
  lastSelectedRowIndexRef,
}: {
  bulkActionFeature: BulkActionFeature<T>
  isLoading: boolean
  lastSelectedRowIndexRef: React.RefObject<number | null>
}): ColumnDef<T> => ({
  id: 'select',
  header: ({ table }) => {
    const allSelectableRowIds = table.options.data
      .filter(row => isRowCheckboxVisible(row) && !isRowCheckboxDisabled(row))
      .map((row, index) => table.options.getRowId!(row, index))
    const selectedSelectableRowIds = allSelectableRowIds.filter(id => table.getState().rowSelection[id])
    const isAllSelectableRowsSelected =
      allSelectableRowIds.length > 0 && selectedSelectableRowIds.length === allSelectableRowIds.length
    const areAllRowsDisabled = table.options.data.every(row => isRowCheckboxDisabled(row))

    return (
      <Checkbox
        checked={isAllSelectableRowsSelected}
        onCheckedChange={checked => {
          if (checked) {
            const newSelection = allSelectableRowIds.reduce<RowSelectionState>(
              (acc, id) => {
                acc[id] = true
                return acc
              },
              { ...table.getState().rowSelection }
            )
            table.setRowSelection(newSelection)
          } else {
            const idsToRemove = new Set(allSelectableRowIds)
            const newSelection = Object.fromEntries(
              Object.entries(table.getState().rowSelection).filter(([id]) => !idsToRemove.has(id))
            )
            table.setRowSelection(newSelection)
          }
        }}
        state={isLoading || table.getState().isBulkActionPending || areAllRowsDisabled ? 'disabled' : 'default'}
        icon="dash"
        ariaLabel="Select all rows"
      />
    )
  },
  cell: ({ row, table }) => {
    if (!isRowCheckboxVisible(row.original)) {
      return null
    }

    const isDisabled = table.getState().isBulkActionPending || isRowCheckboxDisabled(row.original)

    const handleCheckboxClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      if (isDisabled) return

      const currentRowIndex = row.index
      const isShiftClick = event.shiftKey
      const lastIndex = lastSelectedRowIndexRef.current

      if (isShiftClick && lastIndex !== null && lastIndex !== currentRowIndex) {
        // Shift-click: select range between last selected and current row
        const start = Math.min(lastIndex, currentRowIndex)
        const end = Math.max(lastIndex, currentRowIndex)
        const rows = table.getRowModel().rows

        const newSelection = { ...table.getState().rowSelection }

        for (let i = start; i <= end; i++) {
          const rowAtIndex = rows[i]
          if (rowAtIndex && isRowCheckboxVisible(rowAtIndex.original) && !isRowCheckboxDisabled(rowAtIndex.original)) {
            newSelection[rowAtIndex.id] = true
          }
        }

        table.setRowSelection(newSelection)
      } else {
        // Normal click: toggle the row and update last selected index
        row.toggleSelected(!row.getIsSelected())
      }

      lastSelectedRowIndexRef.current = currentRowIndex
    }

    return (
      <div onClick={handleCheckboxClick} style={{ cursor: isDisabled ? 'default' : 'pointer' }}>
        <Checkbox
          state={isDisabled ? 'disabled' : 'default'}
          checked={row.getIsSelected()}
          onCheckedChange={() => {
            // Handled by the parent div's onClick to capture shift key
          }}
        />
      </div>
    )
  },
  meta: {
    width: FIT_CONTENT_WIDTH,
  },
})

export const FIT_CONTENT_WIDTH = '0.1%'

/**
 * Restricts row selection to only main rows (rows with visible checkboxes).
 * Without this, selecting a main row would also add all its sub-rows to the selection state.
 */
const getEnableRowSelectionForMainRowsOnly = <T extends RowData>(
  bulkActionFeature: BulkActionFeature<T> | undefined
): boolean | ((row: Row<T>) => boolean) =>
  bulkActionFeature ? row => bulkActionFeature.isRowCheckboxVisible(row.original) : false
